import { useState, useEffect, useCallback } from "react";
import api from "../api/api";
import Modal from "../components/modal";

// 🎨 Cores Light Mode (Limpo e Profissional)
const categoryColors = {
    "Serviço": "text-blue-600 bg-blue-50 border-blue-200",
    "Venda": "text-emerald-600 bg-emerald-50 border-emerald-200",
    "Manutenção": "text-amber-600 bg-amber-50 border-amber-200",
    "Instalação": "text-purple-600 bg-purple-50 border-purple-200",
    "Outros": "text-slate-600 bg-slate-100 border-slate-200"
};

export default function TasksPage() {
    const [tasks, setTasks] = useState([]);
    const [activeTab, setActiveTab] = useState("pendente"); 
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    const [form, setForm] = useState({
        id: null, title: "", description: "", category: "Serviço",
        task_date: new Date().toISOString().split('T')[0], price: ""
    });

    // 🚀 FUNÇÃO DE BUSCA BLINDADA (Mantida Original)
    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/tasks/");
            const uniqueTasks = Array.from(new Map(res.data.map(item => [item.id, item])).values());
            setTasks(uniqueTasks);
        } catch (err) {
            console.error("Erro ao buscar tarefas", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    // 🚀 ALTERAR STATUS (Mantida Lógica Otimista)
    const handleToggle = async (task) => {
        const newStatus = task.status === "pendente" ? "concluido" : "pendente";
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
        
        try {
            await api.put(`/tasks/${task.id}/toggle`);
        } catch (err) {
            fetchTasks(); // Reverte se der erro
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Deseja apagar esta tarefa?")) return;
        setTasks(prev => prev.filter(t => t.id !== id));
        try {
            await api.delete(`/tasks/${id}`);
        } catch (err) {
            fetchTasks();
        }
    };

    const handleSave = async () => {
        if (!form.title || !form.task_date) return alert("Preencha os campos!");
        const payload = { ...form, price: parseFloat(form.price) || 0.0 };
        try {
            if (form.id) await api.put(`/tasks/${form.id}`, payload);
            else await api.post("/tasks/", payload);
            setIsModalOpen(false);
            fetchTasks(); 
        } catch (err) {
            alert("Erro ao salvar.");
        }
    };

    const filteredTasks = tasks.filter(t => t.status === activeTab);

    const formatMoney = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    const formatDate = (ds) => { 
        if(!ds) return "--/--/--";
        const [y, m, d] = ds.split("-"); 
        return `${d}/${m}/${y}`; 
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] p-2 md:p-8 lg:p-10 animate-in fade-in duration-500 flex flex-col items-center pb-24 lg:pb-10">
            
            <div className="w-full max-w-6xl">
                
                {/* HEADER - RESPONSIVO */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 md:p-8 rounded-[20px] md:rounded-[30px] border border-slate-200 shadow-sm mb-6 md:mb-8">
                    <div>
                        <h1 className="text-2xl md:text-4xl font-black text-slate-800 tracking-tighter uppercase leading-none">
                            Agenda de <span className="text-indigo-600">Serviços</span>
                        </h1>
                        <p className="text-slate-400 font-bold uppercase text-[9px] md:text-[10px] tracking-[0.2em] mt-2">Gestão de Tarefas Operacionais</p>
                    </div>
                    
                    <button 
                        onClick={() => { setForm({ id: null, title: "", description: "", category: "Serviço", task_date: new Date().toISOString().split('T')[0], price: "" }); setIsModalOpen(true); }} 
                        className="w-full md:w-auto px-6 py-4 bg-slate-900 text-white font-black rounded-xl md:rounded-2xl hover:bg-indigo-600 transition-all active:scale-95 text-[10px] tracking-widest uppercase shadow-lg shadow-slate-900/10"
                    >
                        + NOVA TAREFA
                    </button>
                </div>

                {/* ABAS - LARGURA TOTAL NO MOBILE */}
                <div className="flex gap-1.5 mb-6 md:mb-8 bg-white p-1.5 rounded-xl md:rounded-2xl border border-slate-200 w-full sm:w-fit shadow-sm">
                    <button onClick={() => setActiveTab('pendente')} className={`flex-1 sm:flex-none px-4 md:px-8 py-3 rounded-lg md:rounded-xl font-black text-[9px] md:text-[10px] uppercase tracking-widest transition-all ${activeTab === 'pendente' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400'}`}>
                        ⏳ Pendentes ({tasks.filter(t => t.status === 'pendente').length})
                    </button>
                    <button onClick={() => setActiveTab('concluido')} className={`flex-1 sm:flex-none px-4 md:px-8 py-3 rounded-lg md:rounded-xl font-black text-[9px] md:text-[10px] uppercase tracking-widest transition-all ${activeTab === 'concluido' ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-400'}`}>
                        ✅ Concluídas ({tasks.filter(t => t.status === 'concluido').length})
                    </button>
                </div>

                {/* CARDS GRID */}
                {loading ? (
                    <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {filteredTasks.map((task) => (
                            <div key={task.id} className="bg-white border border-slate-200 rounded-[20px] md:rounded-[24px] p-5 md:p-6 flex flex-col justify-between hover:shadow-xl hover:border-indigo-200 transition-all duration-300 group relative overflow-hidden">
                                <div className={`absolute top-0 left-0 w-1.5 h-full ${task.status === 'concluido' ? 'bg-emerald-400' : 'bg-indigo-500'}`}></div>
                                
                                <div className="ml-2">
                                    <div className="flex justify-between items-start mb-3 md:mb-4">
                                        <span className={`px-2 py-1 rounded-md text-[8px] md:text-[9px] font-black uppercase border ${categoryColors[task.category] || categoryColors["Outros"]}`}>{task.category}</span>
                                        <span className="text-[9px] md:text-[10px] font-bold text-slate-400">{formatDate(task.task_date)}</span>
                                    </div>
                                    <h3 className={`text-base md:text-lg font-black leading-tight ${task.status === 'concluido' ? 'text-slate-300 line-through' : 'text-slate-800'}`}>{task.title}</h3>
                                    <p className="text-xs md:text-sm text-slate-500 mt-2 line-clamp-2 min-h-[2.5rem]">{task.description}</p>
                                </div>

                                <div className="mt-6 pt-4 border-t border-slate-50 flex items-center justify-between ml-2">
                                    <span className="text-base md:text-lg font-black text-emerald-600">{task.price > 0 ? formatMoney(task.price) : "--"}</span>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => handleToggle(task)} 
                                            className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl transition-all ${task.status === 'pendente' ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white' : 'bg-slate-100 text-slate-400 hover:bg-amber-500 hover:text-white'}`}
                                            title={task.status === 'pendente' ? 'Concluir' : 'Reabrir'}
                                        >
                                            {task.status === 'pendente' ? '✓' : '↩'}
                                        </button>
                                        
                                        {task.status === 'pendente' && (
                                            <button 
                                                onClick={() => { setForm({ ...task, price: task.price || "" }); setIsModalOpen(true); }} 
                                                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white"
                                            >
                                                ✏️
                                            </button>
                                        )}
                                        
                                        <button 
                                            onClick={() => handleDelete(task.id)} 
                                            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && filteredTasks.length === 0 && (
                    <div className="text-center py-20 text-slate-400 font-bold uppercase text-[10px] tracking-widest">A tranquilidade reina por aqui... ☕</div>
                )}
            </div>

            {/* MODAL - RESPONSIVO E COM SCROLL */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <div className="p-2 md:p-4 bg-white max-h-[85vh] overflow-y-auto custom-scrollbar">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl md:text-2xl font-black uppercase text-slate-800">{form.id ? "Editar" : "Nova"} Tarefa</h2>
                        <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-rose-500 text-xl font-black">✕</button>
                    </div>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="text-[9px] font-black uppercase text-slate-400 ml-2 mb-1 block">Título do Serviço</label>
                            <input className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl outline-none focus:border-indigo-400 font-bold text-sm" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-[9px] font-black uppercase text-slate-400 ml-2 mb-1 block">Categoria</label>
                                <select className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl outline-none font-bold text-sm" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                                    <option value="Serviço">Serviço</option>
                                    <option value="Venda">Venda</option>
                                    <option value="Manutenção">Manutenção</option>
                                    <option value="Instalação">Instalação</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[9px] font-black uppercase text-slate-400 ml-2 mb-1 block">Data</label>
                                <input type="date" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl outline-none font-bold text-sm" value={form.task_date} onChange={e => setForm({...form, task_date: e.target.value})} />
                            </div>
                        </div>

                        <div>
                            <label className="text-[9px] font-black uppercase text-emerald-600 ml-2 mb-1 block">Valor Previsto (R$)</label>
                            <input type="number" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl outline-none focus:border-emerald-400 font-bold text-emerald-600 text-sm" placeholder="0.00" value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
                        </div>

                        <div>
                            <label className="text-[9px] font-black uppercase text-slate-400 ml-2 mb-1 block">Instruções e Detalhes</label>
                            <textarea className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl outline-none h-28 font-medium text-sm" placeholder="Descreva o que precisa ser feito..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                        </div>

                        <button onClick={handleSave} className="w-full py-5 bg-slate-900 text-white font-black rounded-xl md:rounded-2xl hover:bg-indigo-600 transition-all uppercase tracking-widest text-[10px] shadow-lg active:scale-95 mt-2">
                            Salvar Alterações 💾
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}